import React, { useEffect, useState } from 'react';
// styling
import { Colors } from '_styles';
import styled from 'styled-components';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { resetMenuContext, resetFlightContext, selectSelectedFlightForContext } from '_redux/slices/utils/menu-context';
import { setShowAddPassenger, selectTaskList } from '_redux/slices/timeline/timeline-data';
import { setSelectedFlightToView } from "_redux/slices/timeline/selected-task";
import { duplicateBookingLocally, selectAllBookingsByFlight } from '_redux/slices/timeline/timeline-bookings';
import { setToast, setShowToast } from "_redux/slices/timeline/timeline-toast";


// icons
import { IoMdPersonAdd } from "react-icons/io";
import { FaCircleArrowDown, FaCircleArrowUp } from "react-icons/fa6";

import { IoChatbubbleEllipsesOutline, IoEyeOutline } from "react-icons/io5";
import { IoArrowBack } from "react-icons/io5";
import { selectExpandAllFlights, setFlightModal } from '_redux/slices/timeline/timeline-control';
import { getPassengerFlightList, selectExpandedFlights, setExpandedFlights, setSelectedFlightToChat } from '_redux/slices/timeline/flight-information-list';

const StyledParagraph = styled.p`
    cursor: pointer;
    color: ${props => (props.color)};
    font-weight: 500;
    margin-left: 18px;
    margin-top: 12px;
    border-radius: 5px;
    width: 100%;
    font-size: 12px;
    display: flex;
    align-items: center;
`;

const MenuList = styled.div`
    border-style: solid;
    border-width: 1px;
    border-color: lightgray;
    border-radius: 5px;
    background-color: white;
    box-shadow: 0px 0px 5px 0px lightgray;
    max-width: 150px;
`;

const FlightActionMenu = () => {

    //#region redux
    const dispatch = useDispatch();
    const selectedFlight = useSelector(selectSelectedFlightForContext);
    const expandAllFlights = useSelector(selectExpandAllFlights);
    const expandedFlights = useSelector(selectExpandedFlights);                 // list of expanded flights
    //#endregion

    const taskList = useSelector(selectTaskList);

    // Check if this is a boarding or ramp flight
    const isBoardingFlight = selectedFlight?.flightNumber?.includes('/boarding') || selectedFlight?.flightToExpand?.includes('_BOARDING');
    const isRampFlight = selectedFlight?.flightInfo?.type === 'ramp';

    // Get bookings for this flight to find the original boarding/ramp booking
    const flightKey = selectedFlight?.flightToExpand;
    const bookingsForFlight = useSelector(selectAllBookingsByFlight(flightKey));

    //#region local states
    const [hover, setHover] = useState('none');
    const [canAddPax, setCanAddPax] = useState(true);
    const [canShowPax, setCanShowPax] = useState(false);
    const [canHidePax, setCanHidePax] = useState(false);

    //#endregion

    //#region useEffect

    useEffect(() => {
        // Add event listener when the component mounts
        document.addEventListener('click', heardClick);

        // Cleanup the event listener when the component unmounts
        return () => {
            document.removeEventListener('click', heardClick);
        };
    }, []);

    useEffect(() => {
        if (selectedFlight?.flightNumber?.includes("ALTPickUp")) setCanAddPax(false);
        if (expandAllFlights) {
            setCanShowPax(false);
            setCanHidePax(false);
        } else {
            if (expandedFlights?.includes(selectedFlight?.flightToExpand)) {
                setCanShowPax(false);
                setCanHidePax(true)
            } else {
                setCanShowPax(true);
                setCanHidePax(false)
            };

        }

    }, [selectedFlight, expandAllFlights]);
    //#endregion

    //#region functions
    const handleAddPax = () => {
        if (selectedFlight?.flightNumber?.includes("ALTPickUp")) return;

        // For boarding/ramp flights, create a client-side duplicate instead of showing the add passenger modal
        if (isBoardingFlight || isRampFlight) {
            // Find the original booking (not a duplicate) for this flight
            const originalBooking = bookingsForFlight?.find(b =>
                (b.combinedBooking || b.isBoardingBooking || b.combinedRampBooking) && !b._id?.includes('-duplicate-') && !b.isClientDuplicate
            );

            if (!originalBooking) {
                dispatch(setToast({ message: "Cannot add boarding: No original booking found", statusType: "error" }));
                dispatch(setShowToast(true));
                return;
            }

            const baseBookingId = originalBooking._id;

            // Collect IDs of already-assigned duplicate tasks to avoid ID collisions
            const assignedDuplicateIds = Array.isFullArray(taskList)
                ? taskList
                    .filter(task => task?._id?.includes("-duplicate-") && task?._id?.startsWith(baseBookingId))
                    .map(task => task._id)
                : [];

            dispatch(duplicateBookingLocally({
                booking: originalBooking,
                flightKey: flightKey,
                assignedDuplicateIds,
                baseBookingId
            }));

            dispatch(setToast({ message: "Boarding task added successfully", statusType: "success" }));
            dispatch(setShowToast(true));

            // Close the context menu
            dispatch(resetFlightContext());
            dispatch(resetMenuContext());
            return;
        }

        // For regular flights, show the add passenger modal
        let processedFlightNumber = selectedFlight?.flightNumber;
        if (processedFlightNumber?.includes('/boarding')) {
            processedFlightNumber = processedFlightNumber.replace('/boarding', '');
        }
        dispatch(setShowAddPassenger(processedFlightNumber));
    }

    const heardClick = () => {
        if (hover === 'none') {
            dispatch(resetFlightContext());
            dispatch(resetMenuContext());
        }
    };

    const handleBack = () => {
        dispatch(resetFlightContext());
        dispatch(resetMenuContext());
    };

    // show flights
    const handleExpand = () => {
        // check if expandedFlights is an array with length
        if (Array.isFullArray(expandedFlights)) {
            // if expandedFlights includes the current flight, remove it
            if (expandedFlights?.includes(selectedFlight?.flightToExpand)) {
                let tempExpandedFlights = expandedFlights.filter(flight => flight != selectedFlight?.flightToExpand);
                dispatch(setExpandedFlights(tempExpandedFlights));
            } else {
                // if expandedFlights does not include the current flight, add it
                let tempExpandedFlights = [...expandedFlights, selectedFlight?.flightToExpand];
                dispatch(setExpandedFlights(tempExpandedFlights));
            }
        } else {
            // if expandedFlights is not an array, set it to an array with the current flight
            dispatch(setExpandedFlights([selectedFlight?.flightToExpand]));
        }
    };

    const handleViewFLight = () => {

        //console.log('handleViewFLight', selectedFlight);

        const type = selectedFlight?.flightInfo?.type === "arrival" ? "inbound" : "outbound";
        const iata = type === "inbound" ? selectedFlight?.flightInfo?.destination : selectedFlight?.flightInfo?.origin
        dispatch(getPassengerFlightList({
            flightNumber: selectedFlight?.flightInfo.flightNumber,
            departureDate: selectedFlight?.flightInfo.flightDate,
            iata,
            type
        }))
        dispatch(setSelectedFlightToView(selectedFlight?.flightInfo || selectedFlight))
        dispatch(setFlightModal(true))
    };

    const handleViewFlightChat = () => {
        dispatch(setSelectedFlightToChat(selectedFlight?.flightInfo))
    };

    //#endregion
    return (
        <div>
            <MenuList>
                {/*Add PAX / Add Boarding */}
                {canAddPax && <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "Add PAX" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("Add PAX")} onMouseLeave={() => { setHover('none') }} color={hover == "Add PAX" ? 'white' : 'gray'} onClick={handleAddPax}>
                        <IoMdPersonAdd size={20} style={{ marginInlineEnd: "5px" }} />
                        {(isBoardingFlight || isRampFlight) ? "Add Boarding" : "Add PAX"}
                    </StyledParagraph>
                </div>}

                {/*Show PAX List*/}
                {/* {canShowPax && <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "Show PAX List" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("Show PAX List")} onMouseLeave={() => { setHover('none') }} color={hover == "Show PAX List" ? 'white' : 'gray'} onClick={handleExpand}>
                        <FaCircleArrowDown size={20} style={{ marginInlineEnd: "5px" }} />
                        {"Show PAX List"}
                    </StyledParagraph>
                </div>} */}
                {/*Hide PAX List*/}
                {/* {canHidePax && <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "Hide PAX List" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("Hide PAX List")} onMouseLeave={() => { setHover('none') }} color={hover == "Hide PAX List" ? 'white' : 'gray'} onClick={handleExpand}>
                        <FaCircleArrowUp size={20} style={{ marginInlineEnd: "5px" }} />
                        {"Hide PAX List"}
                    </StyledParagraph>
                </div>} */}

                {/*View Flight*/}

                <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "View Flight" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("View Flight")} onMouseLeave={() => { setHover('none') }} color={hover == "View Flight" ? 'white' : 'gray'} onClick={handleViewFLight}>
                        <IoEyeOutline size={20} style={{ marginInlineEnd: "5px" }} />
                        {"View Flight"}
                    </StyledParagraph>
                </div>

                {/*Flight Chat*/}
                <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "Flight Chat" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("Flight Chat")} onMouseLeave={() => { setHover('none') }} color={hover == "Flight Chat" ? 'white' : 'gray'} onClick={handleViewFlightChat}>
                        <IoChatbubbleEllipsesOutline size={20} style={{ marginInlineEnd: "5px" }} />
                        {"Flight Chat"}
                    </StyledParagraph>
                </div>

                <div style={{ display: 'grid', placeItems: 'center', backgroundColor: hover === "Back" ? Colors.PRM_LIGHT_BLUE : 'white', borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                    <StyledParagraph onMouseOver={() => setHover("Back")} onMouseLeave={() => { setHover('none') }} color={hover == "Back" ? 'white' : 'gray'} onClick={handleBack}>
                        <IoArrowBack size={20} style={{ marginInlineEnd: "5px" }} />
                        {"Back"}
                    </StyledParagraph>
                </div>
            </MenuList>
        </div>
    );
};

export default FlightActionMenu;